import React, { useEffect, useState } from 'react'
import BigModal from '../../../components/Modals/BigModal'
import {
  Alert,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import SchoolIcon from '@mui/icons-material/School'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DownloadIcon from '@mui/icons-material/Download'
import QuizIcon from '@mui/icons-material/Quiz'
import Upload from './Upload'
import useDocumentPreview, {
  extensionOf,
} from '../../../components/Documents/useDocumentPreview'
import {
  errorMessage,
  getCertificate,
  isOk,
  startCourse,
} from '../../../training/helpers/trainingApi'
import { downloadCertificatePdf } from '../../../training/components/pdf/certificateDownload'

const buttonSx = { width: { xs: '100%', sm: 'auto' } }

// `selectedCourse.eLearning` is the course's entry from /training/my-courses
// and is only present when the course can be taken as e-learning.
const Popup = ({
  selectedCourse,
  setSelectedCourse,
  getMyMandatoryTraining,
  assessor,
}) => {
  const navigate = useNavigate()
  const [openUploadScreen, setOpenUploadScreen] = useState(false)
  const { openPreview, previewModal } = useDocumentPreview()
  const [busy, setBusy] = useState(null) // 'start' | 'testOut' | 'certificate'
  const [error, setError] = useState('')
  const [pathwayId, setPathwayId] = useState('')

  const course = selectedCourse?.course
  const latestCertificate = selectedCourse?.latestCourse?.certificate
  const eLearning = selectedCourse?.eLearning
  const enrolment = eLearning?.enrolment
  // Course reviewers choose which pathway to open.
  const pathways = assessor && eLearning?.pathways?.length ? eLearning.pathways : null

  useEffect(() => {
    setBusy(null)
    setError('')
    setPathwayId(selectedCourse?.eLearning?.pathwayId ?? '')
  }, [selectedCourse])

  const openCourse = async ({ testOut = false } = {}) => {
    setBusy(testOut ? 'testOut' : 'start')
    setError('')
    const res = await startCourse(course._id, {
      ...(testOut ? { testOut: true } : {}),
      ...(pathways && pathwayId ? { pathwayId } : {}),
    })
    if (!isOk(res) || !res.data?.enrolmentId) {
      setBusy(null)
      setError(errorMessage(res, 'Unable to open the course. Please try again.'))
      return
    }
    navigate(
      `/training/course/${res.data.enrolmentId}${testOut && !res.data.resumed ? '/assessment' : ''}`,
    )
  }

  const downloadELearningCertificate = async () => {
    setBusy('certificate')
    setError('')
    try {
      const res = await getCertificate(eLearning.certificateId)
      if (!isOk(res)) {
        setError(errorMessage(res, 'Unable to load your certificate. Please try again.'))
        return
      }
      await downloadCertificatePdf(res.data)
    } catch (e) {
      console.error(e)
      setError('Unable to create the certificate PDF. Please try again.')
    } finally {
      setBusy(null)
    }
  }

  const startLabel = pathways
    ? 'Open course'
    : enrolment
      ? `Continue course (${enrolment.percentComplete ?? 0}%)`
      : 'Start course'

  return (
    <BigModal
      autoWidth
      open={!!selectedCourse}
      onClose={() => setSelectedCourse(null)}
      // autoWidth collapses to a sliver on phones; use the full width there
      style={{ width: { xs: 'calc(100vw - 32px)', sm: 'auto' } }}
    >
      <Stack gap={1} padding={1} alignItems="center">
        <Typography variant="title2" textAlign="center">
          {course?.title}
        </Typography>
        {eLearning?.pathwayTitle && !pathways && (
          <Typography variant="body2" color="text.secondary">
            {eLearning.pathwayTitle}
          </Typography>
        )}
        {selectedCourse?.legacyOnly && (
          <Typography variant="body2" sx={{ maxWidth: 360 }}>
            Your earlier e-learning result no longer counts. Please take the
            new course.
          </Typography>
        )}
        {eLearning && (
          <>
            {pathways && (
              <FormControl component="fieldset" sx={{ alignSelf: 'stretch' }}>
                <FormLabel component="legend">Pathway to review</FormLabel>
                <RadioGroup
                  name="review-pathway"
                  value={pathwayId}
                  onChange={(event) => setPathwayId(event.target.value)}
                >
                  {pathways.map((pathway) => (
                    <FormControlLabel
                      key={pathway.id}
                      value={pathway.id}
                      control={<Radio />}
                      label={pathway.title}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}
            <Button
              color="success"
              variant="contained"
              onClick={() => openCourse()}
              disabled={!!busy}
              startIcon={<SchoolIcon />}
              sx={buttonSx}
            >
              {busy === 'start' ? 'Opening…' : startLabel}
            </Button>
            {eLearning.canTestOut && !enrolment && !pathways && (
              <>
                <Button
                  variant="outlined"
                  onClick={() => openCourse({ testOut: true })}
                  disabled={!!busy}
                  startIcon={<QuizIcon />}
                  sx={buttonSx}
                >
                  {busy === 'testOut' ? 'Opening…' : 'Take the assessment first'}
                </Button>
                <Typography variant="body2" sx={{ maxWidth: 360 }}>
                  For refreshers: pass the assessment and you do not need to
                  repeat the course. If you do not pass, you take the full
                  course.
                </Typography>
              </>
            )}
            {eLearning.certificateId && (
              <Button
                variant="outlined"
                onClick={downloadELearningCertificate}
                disabled={!!busy}
                startIcon={<DownloadIcon />}
                sx={buttonSx}
              >
                {busy === 'certificate'
                  ? 'Creating PDF…'
                  : 'View certificate (PDF download)'}
              </Button>
            )}
          </>
        )}
        <Button
          color="primary"
          variant="contained"
          onClick={() => setOpenUploadScreen(true)}
          startIcon={<CloudUploadIcon />}
          sx={buttonSx}
        >
          Upload Certificate
        </Button>
        {latestCertificate && (
          <Button
            variant="outlined"
            onClick={() =>
              openPreview({
                key: latestCertificate,
                name: `${course?.title} certificate.${extensionOf(latestCertificate) || 'pdf'}`,
              })
            }
            startIcon={<VisibilityIcon />}
            sx={buttonSx}
          >
            View Latest Certificate
          </Button>
        )}
        <div aria-live="assertive">
          {error && <Alert severity="error">{error}</Alert>}
        </div>
      </Stack>
      <Upload
        open={openUploadScreen}
        onClose={() => setOpenUploadScreen(false)}
        selectedCourse={course}
        successAction={() => {
          setOpenUploadScreen(false)
          getMyMandatoryTraining()
          setSelectedCourse(null)
        }}
      />
      {previewModal}
    </BigModal>
  )
}

export default Popup
